import { useEffect, useState } from "react";
import { Eye, RefreshCw, Search } from "lucide-react";
import api, { errorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import Paginator from "@/components/app/Paginator";
import { Badge } from "@/components/ui/badge";

const ROLE_LABEL = { office_staff: "Office Staff", bank_staff: "Bank Staff", partner: "Partner" };

export default function ImpersonationLogsPage() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/impersonation-logs?page=${page}&page_size=${pageSize}`);
      setRows(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page]);

  const needle = q.trim().toLowerCase();
  const filtered = rows.filter((r) => !needle
    || (r.admin_name || "").toLowerCase().includes(needle)
    || (r.target_name || "").toLowerCase().includes(needle)
    || (r.target_phone || "").toLowerCase().includes(needle)
    || (r.ip || "").toLowerCase().includes(needle));

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
    } catch { return iso; }
  };

  return (
    <div className="card-surface p-6" data-testid="impersonation-logs-page">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="font-heading text-xl text-white flex items-center gap-2"><Eye className="w-4 h-4 text-amber-400"/> Impersonation Audit Log</div>
          <div className="text-sm text-slate-500">Every admin one-click-view-as action, with IP + user-agent.</div>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none"/>
            <input
              data-testid="imp-search"
              value={q}
              onChange={(e)=>setQ(e.target.value)}
              placeholder="Search admin / staff / IP"
              className="pl-8 bg-slate-950 border border-slate-700 rounded-md px-3 h-9 text-sm text-white placeholder:text-slate-500 focus:border-amber-500 outline-none min-w-[240px]"/>
          </div>
          <button
            onClick={load}
            data-testid="imp-refresh"
            className="w-9 h-9 rounded-md bg-slate-800 hover:bg-slate-700 border border-slate-700 flex items-center justify-center text-slate-400">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}/>
          </button>
        </div>
      </div>
      <Table>
        <TableHeader>
          <TableRow className="border-white/5">
            <TableHead>Date &amp; Time</TableHead>
            <TableHead>Admin</TableHead>
            <TableHead>Staff Viewed</TableHead>
            <TableHead>Role</TableHead>
            <TableHead>IP</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filtered.map((r) => (
            <TableRow key={r.id} className="border-white/5" data-testid={`imp-row-${r.id}`}>
              <TableCell className="whitespace-nowrap text-sm text-slate-300">{fmtDate(r.at)}</TableCell>
              <TableCell>
                <div className="text-white">{r.admin_name || "Admin"}</div>
                <div className="text-[11px] font-mono-alt text-slate-500">{r.admin_phone || ""}</div>
              </TableCell>
              <TableCell>
                <div className="text-white">{r.target_name || "—"}</div>
                <div className="text-[11px] font-mono-alt text-slate-500">{r.target_phone || ""}</div>
              </TableCell>
              <TableCell>
                <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/30">{ROLE_LABEL[r.target_role] || r.target_role}</Badge>
              </TableCell>
              <TableCell className="font-mono-alt text-xs text-slate-400">{r.ip || "—"}</TableCell>
            </TableRow>
          ))}
          {loading && rows.length === 0 && (
            <TableRow><TableCell colSpan={5} className="text-center py-10">
              <div className="flex items-center justify-center gap-2 text-slate-400"><RefreshCw className="w-4 h-4 animate-spin"/> Loading impersonation logs…</div>
            </TableCell></TableRow>
          )}
          {!loading && filtered.length === 0 && (
            <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-8">No impersonation activity yet</TableCell></TableRow>
          )}
        </TableBody>
      </Table>
      {total > 0 && (
        <Paginator page={page} pageSize={pageSize} total={total} onPage={setPage} testid="imp-pager"/>
      )}
    </div>
  );
}
